import React, { useState, useEffect, useRef } from 'react';
import { BRAND_LOGO_PUBLIC_PATH } from '../config/branding';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
  onLoad?: () => void;
  onError?: () => void;
  placeholder?: React.ReactNode;
}

// كاش بسيط للصور المحملة
const imageCache = new Set<string>();
const failedImages = new Set<string>();

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className = '',
  fallbackSrc = BRAND_LOGO_PUBLIC_PATH,
  onLoad,
  onError,
  placeholder
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const fullSrc = src;

  useEffect(() => {
    // التحقق من الكاش
    if (imageCache.has(fullSrc)) {
      setIsLoaded(true);
      return;
    }

    if (failedImages.has(fullSrc)) {
      setHasError(true);
      return;
    }

    // إعداد Intersection Observer للتحميل التدريجي
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observerRef.current?.disconnect();
        }
      },
      {
        rootMargin: '50px' // بدء التحميل قبل 50px من ظهور الصورة
      }
    );

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [fullSrc]);

  const handleLoad = () => {
    setIsLoaded(true);
    imageCache.add(fullSrc);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    failedImages.add(fullSrc);
    onError?.();
  };

  const shouldShowImage = isInView || imageCache.has(fullSrc);
  const imageSrc = hasError ? fallbackSrc : fullSrc;

  return (
    <div ref={imgRef} className={`relative ${className}`}>
      {shouldShowImage ? (
        <img
          src={imageSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={`transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          } ${className}`}
          onLoad={handleLoad}
          onError={handleError}
        />
      ) : (
        placeholder || (
          <div className={`bg-gray-200 animate-pulse flex items-center justify-center ${className}`}>
            <div className="w-8 h-8 bg-gray-400 rounded-full"></div>
          </div>
        )
      )}
      
      {/* مؤشر التحميل */}
      {shouldShowImage && !isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default LazyImage;